import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { reportsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import { fmt } from "./reportUtils";

interface ReserveFundReportProps {
  period: string;
}

const ReserveFundReport: React.FC<ReserveFundReportProps> = ({ period }) => {
  const { currentBuilding } = useBuilding();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);

  useEffect(() => {
    if (!currentBuilding?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await reportsApi.getReserveFund(
          currentBuilding.id,
          period,
        );
        if (!cancelled) setReport(data);
      } catch (err) {
        console.error("Error fetching reserve fund:", err);
        if (!cancelled)
          setError(
            "Failed to load reserve fund report. Please try again later.",
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentBuilding?.id, period]);

  if (loading) {
    return <Spinner message="Loading reserve fund report..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  if (!report) {
    return <ErrorAlert message="No reserve fund data available" />;
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Reserve Fund Report
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        {report.startDate} to {report.endDate}
      </p>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-xs font-medium text-gray-500 uppercase">
              Opening Balance
            </dt>
            <dd className="mt-2 text-2xl font-semibold text-gray-900">
              {fmt(report.totalOpeningBalance || 0)}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-xs font-medium text-gray-500 uppercase">
              Contributions
            </dt>
            <dd className="mt-2 text-2xl font-semibold text-green-600">
              {fmt(report.totalContributions || 0)}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-xs font-medium text-gray-500 uppercase">
              Withdrawals
            </dt>
            <dd className="mt-2 text-2xl font-semibold text-red-600">
              {fmt(report.totalWithdrawals || 0)}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-xs font-medium text-gray-500 uppercase">
              Closing Balance
            </dt>
            <dd className="mt-2 text-2xl font-semibold text-primary-600">
              {fmt(report.totalClosingBalance || 0)}
            </dd>
          </div>
        </div>
      </div>

      {/* Per-Fund Breakdown Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg font-medium text-gray-900">Fund Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fund Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purpose
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Opening
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contributions
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Withdrawals
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Closing Balance
                </th>
                {report.funds?.some((f: any) => f.targetAmount) && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % of Target
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(report.funds || []).map((fund: any) => (
                <tr key={fund.fundId}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {fund.fundName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {fund.purpose}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {fmt(fund.openingBalance || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                    {fmt(fund.contributions || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600">
                    {fmt(fund.withdrawals || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                    {fmt(fund.closingBalance || 0)}
                  </td>
                  {report.funds?.some((f: any) => f.targetAmount) && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {fund.percentOfTarget
                        ? `${fund.percentOfTarget.toFixed(1)}%`
                        : "-"}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReserveFundReport;
